import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Plus, Lightbulb, Search, MoreVertical, Trash2, Edit, ArrowUpDown,
  ArrowRightCircle, Clock, User, Check, X, Folder, ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter 
} from '../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import { RichTextEditor } from '../components/RichTextEditor';

import config from '../config';
const API = `${config.BACKEND_URL}/api`;

const PRIORITIES = [
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-800 border-red-300' },
  { value: 'very-high', label: 'Very High', color: 'bg-orange-100 text-orange-800 border-orange-300' },
  { value: 'high', label: 'High', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-800 border-gray-300' },
];

export const Ideas = () => {
  const [ideas, setIdeas] = useState([]);
  const [projects, setProjects] = useState([]);
  const [releases, setReleases] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    project_id: ''
  });
  
  // Convert form state
  const [convertForm, setConvertForm] = useState({
    project_id: '',
    release_id: '',
    assigned_users: []
  });
  
  // Filter and sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  // Multi-select project filter: array of project ids (as strings) and/or
  // 'none' for ideas with no project. Empty array = all projects.
  const [filterProjects, setFilterProjects] = useState([]);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const fetchIdeas = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/ideas`, {
        params: {
          search: searchQuery,
          priority: filterPriority,
          sort_by: sortBy,
          sort_order: sortOrder
        }
      });
      setIdeas(response.data);
    } catch (error) {
      toast.error('Failed to load ideas');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filterPriority, sortBy, sortOrder]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  useEffect(() => {
    fetchIdeas();
    fetchProjects();
    fetchUsers();
  }, [fetchIdeas]);

  const fetchReleasesForProject = async (projectId) => {
    try {
      const response = await axios.get(`${API}/releases`, {
        params: { project_id: projectId }
      });
      setReleases(response.data);
    } catch (error) {
      console.error('Failed to load releases');
      setReleases([]);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        project_id: form.project_id ? parseInt(form.project_id) : null
      };
      await axios.post(`${API}/ideas`, payload);
      toast.success('Idea created successfully');
      setCreateDialogOpen(false);
      setForm({ title: '', description: '', priority: 'medium', project_id: '' });
      fetchIdeas();
    } catch (error) {
      toast.error('Failed to create idea');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedIdea) return;
    
    try {
      const payload = {
        ...form,
        project_id: form.project_id ? parseInt(form.project_id) : null
      };
      await axios.put(`${API}/ideas/${selectedIdea.id}`, payload);
      toast.success('Idea updated successfully');
      setEditDialogOpen(false);
      setSelectedIdea(null);
      fetchIdeas();
    } catch (error) {
      toast.error('Failed to update idea');
    }
  };

  const handleDelete = async (ideaId) => {
    if (!window.confirm('Are you sure you want to delete this idea?')) return;
    
    try {
      await axios.delete(`${API}/ideas/${ideaId}`);
      toast.success('Idea deleted');
      fetchIdeas();
    } catch (error) {
      toast.error('Failed to delete idea');
    }
  };

  const openEditDialog = (idea) => {
    setSelectedIdea(idea);
    setForm({
      title: idea.title,
      description: idea.description,
      priority: idea.priority,
      project_id: idea.project_id ? String(idea.project_id) : ''
    });
    setEditDialogOpen(true);
  };

  const openConvertDialog = (idea) => {
    setSelectedIdea(idea);
    setConvertForm({ project_id: '', release_id: '', assigned_users: [] });
    setReleases([]);
    setConvertDialogOpen(true);
  };

  const handleConvert = async () => {
    if (!selectedIdea || !convertForm.project_id || !convertForm.release_id) {
      toast.error('Please select a project and release');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/ideas/${selectedIdea.id}/convert`, {
        project_id: parseInt(convertForm.project_id),
        release_id: parseInt(convertForm.release_id),
        assigned_users: convertForm.assigned_users
      });
      toast.success(`Idea converted to user story ${response.data.issue_number}`);
      setConvertDialogOpen(false);
      setSelectedIdea(null);
      fetchIdeas();
    } catch (error) {
      toast.error('Failed to convert idea to user story');
    }
  };

  const getPriorityBadge = (priority) => {
    const p = PRIORITIES.find(pr => pr.value === priority) || PRIORITIES[3];
    return <Badge variant="outline" className={`${p.color} border`}>{p.label}</Badge>;
  };

  const toggleConvertAssignee = (userId) => {
    setConvertForm(prev => ({
      ...prev,
      assigned_users: prev.assigned_users.includes(userId)
        ? prev.assigned_users.filter(id => id !== userId)
        : [...prev.assigned_users, userId]
    }));
  };

  const toggleProjectFilter = (value) => {
    setFilterProjects((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const projectFilterLabel = (() => {
    if (filterProjects.length === 0) return 'All Projects';
    if (filterProjects.length === 1) {
      if (filterProjects[0] === 'none') return 'No Project';
      const project = projects.find((p) => String(p.id) === filterProjects[0]);
      return project ? project.name : '1 project';
    }
    return `${filterProjects.length} projects`;
  })();

  // Ideas filtered by the project multi-select, applied client-side since
  // all ideas are already fetched (the backend has no project filter param).
  const filteredIdeas = filterProjects.length === 0
    ? ideas
    : ideas.filter((idea) =>
        filterProjects.includes(idea.project_id ? String(idea.project_id) : 'none')
      );

  // Group the (already filtered/sorted) ideas by project, preserving the
  // per-idea order chosen above within each group. Ideas with no project
  // land in a catch-all group, sorted last.
  const ideaGroups = (() => {
    const byKey = new Map();
    for (const idea of filteredIdeas) {
      const key = idea.project_id ?? 'none';
      if (!byKey.has(key)) {
        byKey.set(key, { projectName: idea.project_name || 'No Project', items: [] });
      }
      byKey.get(key).items.push(idea);
    }
    return [...byKey.entries()].sort(([keyA, a], [keyB, b]) => {
      if (keyA === 'none') return 1;
      if (keyB === 'none') return -1;
      return a.projectName.localeCompare(b.projectName);
    });
  })();

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ideas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Lightbulb className="h-8 w-8 text-amber-500" />
            Ideas & Wishes
          </h1>
          <p className="text-muted-foreground mt-1">
            Capture ideas and wishes before they become user stories
          </p>
        </div>
        
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="create-idea-btn">
              <Plus className="h-4 w-4" />
              New Idea
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Add New Idea</DialogTitle>
              <DialogDescription>
                Capture a new idea or wish for future consideration
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Enter idea title..."
                  required
                  data-testid="idea-title-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="project">Project (optional)</Label>
                  <Select
                    value={form.project_id || "none"}
                    onValueChange={(value) => setForm({ ...form, project_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="idea-project-select">
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No project</SelectItem>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={String(project.id)}>
                          {project.prefix} - {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <Select
                    value={form.priority}
                    onValueChange={(value) => setForm({ ...form, priority: value })}
                  >
                    <SelectTrigger data-testid="idea-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <RichTextEditor
                  id="description"
                  value={form.description}
                  onChange={(value) => setForm({ ...form, description: value })}
                  placeholder="Describe your idea..."
                  maxLength={5000}
                  data-testid="idea-description-input"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="idea-submit-btn">Create</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search ideas..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-ideas-input"
              />
            </div>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-[150px]" data-testid="filter-priority-select">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Multi-select project filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-[180px] justify-between gap-2" data-testid="filter-project-select">
                  <span className="truncate flex items-center gap-2">
                    <Folder className="h-4 w-4 text-muted-foreground shrink-0" />
                    {projectFilterLabel}
                  </span>
                  <ChevronDown className="h-4 w-4 ml-2 opacity-50 shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="start">
                <div
                  className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-muted rounded-sm"
                  onClick={() => setFilterProjects([])}
                  data-testid="filter-project-all"
                >
                  <div className={`w-4 h-4 border rounded flex items-center justify-center ${filterProjects.length === 0 ? 'bg-primary border-primary' : 'border-input'}`}>
                    {filterProjects.length === 0 && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                  <span className="text-sm">All Projects</span>
                </div>
                <DropdownMenuSeparator />
                {projects.map((project) => {
                  const value = String(project.id);
                  const checked = filterProjects.includes(value);
                  return (
                    <div
                      key={project.id}
                      className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-muted rounded-sm"
                      onClick={() => toggleProjectFilter(value)}
                      data-testid={`filter-project-${project.id}`}
                    >
                      <div className={`w-4 h-4 border rounded flex items-center justify-center ${checked ? 'bg-primary border-primary' : 'border-input'}`}>
                        {checked && <Check className="h-3 w-3 text-primary-foreground" />}
                      </div>
                      <span className="text-sm truncate">{project.prefix} - {project.name}</span>
                    </div>
                  );
                })}
                <div
                  className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-muted rounded-sm"
                  onClick={() => toggleProjectFilter('none')}
                  data-testid="filter-project-none"
                >
                  <div className={`w-4 h-4 border rounded flex items-center justify-center ${filterProjects.includes('none') ? 'bg-primary border-primary' : 'border-input'}`}>
                    {filterProjects.includes('none') && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                  <span className="text-sm">No Project</span>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <ArrowUpDown className="h-4 w-4" />
                  Sort
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => toggleSort('created_at')}>
                  Date {sortBy === 'created_at' && (sortOrder === 'desc' ? '↓' : '↑')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => toggleSort('title')}>
                  Title {sortBy === 'title' && (sortOrder === 'desc' ? '↓' : '↑')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => toggleSort('priority')}>
                  Priority {sortBy === 'priority' && (sortOrder === 'desc' ? '↓' : '↑')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>

      {/* Ideas List */}
      {ideas.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Lightbulb className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-1">No ideas yet</h3>
            <p className="text-muted-foreground text-center mb-4">
              Start capturing your ideas and wishes for future development
            </p>
            <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Add First Idea
            </Button>
          </CardContent>
        </Card>
      ) : filteredIdeas.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Folder className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-1">No ideas match this filter</h3>
            <p className="text-muted-foreground text-center mb-4">
              Try selecting a different project, or clear the project filter
            </p>
            <Button variant="outline" onClick={() => setFilterProjects([])} className="gap-2">
              <X className="h-4 w-4" />
              Clear Project Filter
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {ideaGroups.map(([key, group]) => (
            <Card key={key}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Folder className="h-4 w-4 text-muted-foreground" />
                  {group.projectName}
                  <Badge variant="secondary" className="ml-1">{group.items.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {group.items.map((idea, index) => (
                    <div
                      key={idea.id}
                      className={`flex items-center justify-between gap-4 px-6 py-3 hover:bg-muted transition-colors ${index % 2 === 1 ? 'bg-muted/40' : ''}`}
                      data-testid={`idea-row-${idea.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">{idea.title}</span>
                          {getPriorityBadge(idea.priority)}
                        </div>
                        {idea.description && (
                          <div
                            className="text-sm text-muted-foreground line-clamp-1 mt-0.5"
                            dangerouslySetInnerHTML={{ __html: idea.description }}
                          />
                        )}
                        <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {idea.created_by_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {format(new Date(idea.created_at), 'MMM d, yyyy')}
                          </span>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(idea)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openConvertDialog(idea)}>
                            <ArrowRightCircle className="h-4 w-4 mr-2" />
                            Convert to Story
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => handleDelete(idea.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Idea</DialogTitle>
            <DialogDescription>Update the idea details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Title *</Label>
              <Input
                id="edit-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
                data-testid="edit-idea-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-project">Project (optional)</Label>
                <Select
                  value={form.project_id || "none"}
                  onValueChange={(value) => setForm({ ...form, project_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="edit-idea-project-select">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No project</SelectItem>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={String(project.id)}>
                        {project.prefix} - {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-priority">Priority</Label>
                <Select
                  value={form.priority}
                  onValueChange={(value) => setForm({ ...form, priority: value })}
                >
                  <SelectTrigger data-testid="edit-idea-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <RichTextEditor
                id="edit-description"
                value={form.description}
                onChange={(value) => setForm({ ...form, description: value })}
                placeholder="Describe your idea..."
                maxLength={5000}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Convert to Story Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Convert to User Story</DialogTitle>
            <DialogDescription>
              Select a project and release to create a user story from this idea
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedIdea && (
              <div className="p-3 bg-muted rounded-md">
                <p className="font-medium">{selectedIdea.title}</p>
                <p className="text-sm text-muted-foreground">Priority: {selectedIdea.priority}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Project *</Label>
              <Select
                value={convertForm.project_id}
                onValueChange={(value) => {
                  setConvertForm({ ...convertForm, project_id: value, release_id: '' });
                  fetchReleasesForProject(parseInt(value));
                }}
              >
                <SelectTrigger data-testid="convert-project-select">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={String(project.id)}>
                      {project.prefix} - {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Release *</Label>
              <Select
                value={convertForm.release_id}
                onValueChange={(value) => setConvertForm({ ...convertForm, release_id: value })}
                disabled={!convertForm.project_id}
              >
                <SelectTrigger data-testid="convert-release-select">
                  <SelectValue placeholder={convertForm.project_id ? "Select release" : "Select project first"} />
                </SelectTrigger>
                <SelectContent>
                  {releases.map((release) => (
                    <SelectItem key={release.id} value={String(release.id)}>
                      v{release.version} - {release.status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Assign Users</Label>
              <div className="border rounded-md max-h-32 overflow-y-auto">
                {users.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No users available</p>
                ) : (
                  <div className="p-2 space-y-1">
                    {users.map((user) => {
                      const isChecked = convertForm.assigned_users.includes(user.id);
                      return (
                        <div
                          key={user.id}
                          className="flex items-center gap-3 p-2 rounded hover:bg-muted cursor-pointer"
                          onClick={() => toggleConvertAssignee(user.id)}
                        >
                          <div className={`h-4 w-4 rounded border flex items-center justify-center ${isChecked ? 'bg-primary border-primary' : 'border-input'}`}>
                            {isChecked && <Check className="h-3 w-3 text-primary-foreground" />}
                          </div>
                          <span className="text-sm">{user.name}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setConvertDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConvert} 
              disabled={!convertForm.project_id || !convertForm.release_id}
              className="gap-2"
            >
              <ArrowRightCircle className="h-4 w-4" />
              Convert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
